import { useState, useEffect, useRef } from "react";
import useInView from "@/hooks/useInView";

const defaultEarningsData = {
  studio: { label: "Studio", grossLow: 1500, grossHigh: 2100, astGross: 800, blurb: "Consistently above long-let equivalents, particularly close to MediaCityUK." },
  "1bed": { label: "1 bed", grossLow: 1900, grossHigh: 3100, astGross: 1100, blurb: "Our most common property type in the city. Performs reliably year-round." },
  "2bed": { label: "2 beds", grossLow: 2800, grossHigh: 4600, astGross: 1500, blurb: "Corporate stays, relocations and weekend groups, on top of leisure demand." },
  "3bed": { label: "3+ beds", grossLow: 3500, grossHigh: 8000, astGross: 1900, blurb: "Family-sized homes south of the city command premium rates in summer and around events." },
};

const defaultSeasonalPattern = [
  { month: "Jan", val: 55 }, { month: "Feb", val: 52 }, { month: "Mar", val: 65 },
  { month: "Apr", val: 70 }, { month: "May", val: 75 }, { month: "Jun", val: 85 },
  { month: "Jul", val: 92 }, { month: "Aug", val: 100 }, { month: "Sep", val: 80 },
  { month: "Oct", val: 62 }, { month: "Nov", val: 62 }, { month: "Dec", val: 72 },
];

const NET_RATE = 0.676;

function useCountUp(target, active, duration = 900) {
  const [value, setValue] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const from = prevRef.current;
    const raf = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) requestAnimationFrame(raf);
    };
    const id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); prevRef.current = target; };
  }, [target, active, duration]);
  return value;
}

function BedSelector({ value, onChange }) {
  const opts = [
    { key: "studio", label: "Studio" },
    { key: "1bed", label: "1 bed" },
    { key: "2bed", label: "2 beds" },
    { key: "3bed", label: "3+ beds" },
  ];
  return (
    <div className="ee-bed-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {opts.map((o) => {
        const sel = value === o.key;
        return (
          <button key={o.key} type="button" onClick={() => onChange(o.key)}
            style={{
              padding: "10px 18px",
              border: sel ? "2px solid #112215" : "1.5px solid #D5D0BE",
              borderRadius: 10,
              background: sel ? "#112215" : "#fff",
              fontFamily: "'Inter', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: sel ? "#F4F1E7" : "#112215",
              cursor: "pointer",
              transition: "all 0.2s",
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function smoothPath(points) {
  if (points.length < 2) return "";
  let d = `M${points[0][0]},${points[0][1]}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cx = (prev[0] + curr[0]) / 2;
    d += ` C${cx},${prev[1]} ${cx},${curr[1]} ${curr[0]},${curr[1]}`;
  }
  return d;
}

function parseEarningsArray(earnings) {
  if (!earnings || earnings.length === 0) return null;
  const keyMap = { "Studio": "studio", "1 Bedroom": "1bed", "2 Bedroom": "2bed", "3 Bedroom": "3bed", "3+ Bedroom": "3bed", "4+ Bedroom": "3bed" };
  const labelMap = { "Studio": "Studio", "1 Bedroom": "1 bed", "2 Bedroom": "2 beds", "3 Bedroom": "3+ beds", "3+ Bedroom": "3+ beds", "4+ Bedroom": "3+ beds" };
  const data = {};
  for (const item of earnings) {
    const key = keyMap[item.type];
    if (!key || data[key]) continue;
    const nums = item.monthly.replace(/[£,\sAED]/g, "").split(/[–-]/).map(Number);
    const grossLow = nums[0] || 0;
    const grossHigh = nums[1] || grossLow;
    const upliftMatch = (item.uplift || "").match(/(\d+)/);
    const upliftPct = upliftMatch ? parseInt(upliftMatch[1]) : 50;
    const astGross = Math.round(grossLow / (1 + upliftPct / 100));
    data[key] = { label: labelMap[item.type] || item.type, grossLow, grossHigh, astGross, blurb: item.description || "" };
  }
  return Object.keys(data).length > 0 ? data : null;
}

export default function ManchesterEarningsExplorer({ config }) {
  const earningsData = config?.earningsData || parseEarningsArray(config?.earnings) || defaultEarningsData;
  const seasonalPattern = config?.seasonalPattern || defaultSeasonalPattern;
  const cityName = config?.cityName || "Manchester";
  const [ref, inView] = useInView({ threshold: 0.15 });
  const [bedType, setBedType] = useState("2bed");

  const data = earningsData[bedType];
  const netLow = Math.round(data.grossLow * NET_RATE);
  const netHigh = Math.round(data.grossHigh * NET_RATE);
  const uplift = Math.round(((data.grossLow + data.grossHigh) / 2 - data.astGross) / data.astGross * 100);

  const grossLowAnim = useCountUp(data.grossLow, inView);
  const grossHighAnim = useCountUp(data.grossHigh, inView, 1100);
  const netLowAnim = useCountUp(netLow, inView);
  const netHighAnim = useCountUp(netHigh, inView, 1100);

  // Line chart
  const w = 100, h = 36, max = 100;
  const points = seasonalPattern.map((m, i) => [(i / (seasonalPattern.length - 1)) * w, h - (m.val / max) * h]);
  const linePath = smoothPath(points);
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;

  return (
    <section ref={ref} style={{ background: "linear-gradient(to bottom, #FFFFFF 0%, #F4F1E7 100%)", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 0.85fr", gap: "clamp(56px,7vw,96px)", alignItems: "center" }} className="ee-split-grid">
          {/* Left: text + selector (vertically centered) */}
          <div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>
              Earnings explorer
            </p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px,4vw,40px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 0 16px" }}>
              What could your property earn?
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif",               fontSize: 16, color: "#5C7268", lineHeight: 1.65, margin: "0 0 12px" }}>
              Pick your property type to see indicative monthly ranges from our live {cityName} data. Figures come from real properties we manage across the city, with dynamic pricing adjusted for events, seasonality and local demand.
            </p>
            <BedSelector value={bedType} onChange={setBedType} />
            <p style={{ fontFamily: "'Inter', sans-serif",               fontSize: 13.5, color: "#5C7268", lineHeight: 1.6, margin: "16px 0 0" }}>
              {data.blurb}
            </p>
          </div>

          {/* Right: receipt with zigzag bottom */}
          <div>
            <div style={{ background: "#FFFFFF", borderRadius: "16px 16px 0 0", padding: "22px 26px", boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 12px 32px rgba(0,0,0,0.06)" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#5C7268" }}>
                  Monthly estimate
                </span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 700, color: "#112215", background: "#E8F7D9", padding: "4px 12px", borderRadius: 100 }}>
                  {data.label}
                </span>
              </div>

              {/* Gross revenue */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A9B92", marginBottom: 4, marginTop: 0 }}>
                  Gross revenue
                </p>
                <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", letterSpacing: "-0.02em" }}>
                    &pound;{grossLowAnim.toLocaleString("en-GB")}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#8A9B92" }}>–</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", letterSpacing: "-0.02em" }}>
                    &pound;{grossHighAnim.toLocaleString("en-GB")}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#8A9B92" }}>/mo</span>
                </div>
              </div>

              {/* Fees (compact) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontFamily: "'Inter', sans-serif" }}>
                  <span style={{ color: "#8A9B92" }}>Platform fees</span>
                  <span style={{ fontWeight: 600, color: "#112215" }}>&minus;18%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontFamily: "'Inter', sans-serif" }}>
                  <span style={{ color: "#8A9B92" }}>Management &amp; VAT</span>
                  <span style={{ fontWeight: 600, color: "#112215" }}>&minus;14.4%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontFamily: "'Inter', sans-serif" }}>
                  <span style={{ color: "#8A9B92" }}>Cleaning &amp; linen</span>
                  <span style={{ fontWeight: 600, color: "#112215" }}>&pound;0 (guest pays)</span>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: "2px solid #112215", margin: "10px 0 12px" }} />

              {/* Net payout */}
              <div style={{ background: "#E8F7D9", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#112215", marginBottom: 4, marginTop: 0 }}>
                  Your net payout
                </p>
                <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 19, color: "#112215", letterSpacing: "-0.02em" }}>
                    &pound;{netLowAnim.toLocaleString("en-GB")}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#5C7268" }}>–</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 19, color: "#112215", letterSpacing: "-0.02em" }}>
                    &pound;{netHighAnim.toLocaleString("en-GB")}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#5C7268" }}>/mo</span>
                </div>
              </div>

              {/* vs AST — clear visual comparison */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#FFFFFF", borderRadius: 10, marginBottom: 16, border: "1px solid #E8E4D5" }}>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8A9B92", margin: 0 }}>AST</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: "#8A9B92", margin: "2px 0 0" }}>&pound;{data.astGross.toLocaleString("en-GB")}/mo</p>
                </div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "#B8B4A5" }}>&rarr;</span>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8A9B92", margin: 0 }}>Short-let</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: "#112215", margin: "2px 0 0" }}>&pound;{data.grossLow.toLocaleString("en-GB")}–&pound;{data.grossHigh.toLocaleString("en-GB")}/mo</p>
                </div>
                <span style={{ marginLeft: "auto", fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700, color: "#112215", background: "#E8F7D9", padding: "3px 10px", borderRadius: 100, whiteSpace: "nowrap" }}>+{uplift}%</span>
              </div>

              {/* Line chart */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A9B92" }}>
                    Seasonal pattern
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, color: "#112215", background: "#E8F7D9", padding: "2px 8px", borderRadius: 100 }}>
                    Peak: Aug
                  </span>
                </div>
                <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: "block" }}>
                  <defs>
                    <linearGradient id="ee-line-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#112215" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="#112215" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={areaPath} fill="url(#ee-line-grad)" opacity={inView ? 1 : 0} style={{ transition: "opacity 0.6s 0.3s" }} />
                  <path d={linePath} fill="none" stroke="#112215" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="400" strokeDashoffset={inView ? 0 : 400} style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
                </svg>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                  {seasonalPattern.map((m) => (
                    <span key={m.month} style={{ fontSize: 7, color: "#8A9B92", fontFamily: "'Inter', sans-serif", textAlign: "center", flex: 1 }}>
                      {m.month}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {/* Zigzag bottom border */}
            <svg style={{ display: "block", marginTop: -1 }} width="100%" height="8" viewBox="0 0 200 8" preserveAspectRatio="none">
              <path d="M0,0 L4,8 L8,0 L12,8 L16,0 L20,8 L24,0 L28,8 L32,0 L36,8 L40,0 L44,8 L48,0 L52,8 L56,0 L60,8 L64,0 L68,8 L72,0 L76,8 L80,0 L84,8 L88,0 L92,8 L96,0 L100,8 L104,0 L108,8 L112,0 L116,8 L120,0 L124,8 L128,0 L132,8 L136,0 L140,8 L144,0 L148,8 L152,0 L156,8 L160,0 L164,8 L168,0 L172,8 L176,0 L180,8 L184,0 L188,8 L192,0 L196,8 L200,0 Z" fill="#FFFFFF" />
            </svg>
          </div>
        </div>

      </div>

      <style>{`
        @media (max-width: 820px) { .ee-split-grid { grid-template-columns: 1fr !important; gap: 40px !important; } }
        @media (max-width: 520px) { .ee-bed-row button { flex: 1 1 calc(50% - 4px) !important; } }
      `}</style>
    </section>
  );
}